import React, { useEffect, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ArrowLeft, ClipboardList, FileText, AlertTriangle, DollarSign, Boxes, Hash,
  CheckCircle2, Calendar, User, History, Trash2, Send, QrCode, Printer, Layers, GitBranch,
  Link2, Plus, FileImage, FileCog, Paperclip, ShieldCheck, ExternalLink, Download, Tag, FlaskConical,
} from 'lucide-react';
import { SendToRDDialog } from '../components/rd/SendToRDDialog';
import { BomLineageTimeline } from '../components/rd/BomLineageTimeline';
import { AssemblyLineBadge, RolledCostCard } from '../components/RolledCost';
import { QRCodeSVG } from 'qrcode.react';
import { openLetterheadWindow } from '../utils/brandedPrint';
import { toast } from 'sonner';
import PrintLabelDialog from '../components/PrintLabelDialog';
import BomLinkedFilesPanel from '../components/BomLinkedFilesPanel';
import { STICKER_SIZES } from '../components/PrintLabelDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString() : '—');
const fmtDateTime = (s) => (s ? new Date(s).toLocaleString() : '—');

const StatCard = ({ icon: Icon, label, value, subtle, accent }) => (
  <div className="rounded-sm border border-slate-200 p-3" data-testid={`bom-stat-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1">
      {Icon && <Icon className="w-3 h-3" />} {label}
    </div>
    <div className={`text-2xl font-bold mono ${accent || 'text-slate-900'}`}>{value}</div>
    {subtle && <div className="text-xs text-slate-500">{subtle}</div>}
  </div>
);

// iter78t — Inline per-BOM default label-size picker. Auto-saves on change.
const BomLabelSizePicker = ({ bom, onSaved }) => {
  const { api } = useAuth();
  const [val, setVal] = useState(bom?.default_label_size_id || 'none');
  const [busy, setBusy] = useState(false);

  useEffect(() => { setVal(bom?.default_label_size_id || 'none'); }, [bom?.default_label_size_id]);

  const handleChange = async (next) => {
    setVal(next);
    setBusy(true);
    try {
      const payload = {
        product_type_id: bom.product_type_id,
        bom_variant_code: bom.bom_variant_code,
        bom_revision: bom.bom_revision,
        status: bom.status,
        effective_date: bom.effective_date,
        notes: bom.notes || '',
        next_revision_date: bom.next_revision_date,
        developed_by: bom.developed_by,
        description: bom.description,
        require_matched_file_to_release: !!bom.require_matched_file_to_release,
        parent_bom_id: bom.parent_bom_id,
        archived: bom.archived,
        archived_at: bom.archived_at,
        archived_by: bom.archived_by,
        default_label_size_id: next === 'none' ? null : next,
        photos: bom.photos || null,
        lines: (bom.lines || []).map(l => ({
          component_sku: l.component_sku,
          quantity_per_unit: parseFloat(l.quantity_per_unit) || 0,
          uom: l.uom || 'EA',
          scrap_factor_percent: parseFloat(l.scrap_factor_percent) || 0,
          substitutes_allowed: !!l.substitutes_allowed,
        })),
      };
      await api().put(`/boms/${bom.id}`, payload);
      toast.success(next === 'none' ? 'Default label size cleared' : `Default label size: ${STICKER_SIZES.find(s => s.id === next)?.label || next}`);
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Select value={val} onValueChange={handleChange} disabled={busy}>
      <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="bom-default-label-size">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="none">— Pick at print time —</SelectItem>
        {STICKER_SIZES.map(s => (
          <SelectItem key={s.id} value={s.id} data-testid={`bom-default-label-size-option-${s.id}`}>{s.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};



const BomDetailPage = () => {
  const { id } = useParams();
  const { api, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  // iter73 — preserve the active tab across data refetches (the previous default
  // re-mounted the Tabs and reset back to "components" after every mutation).
  const [activeTab, setActiveTab] = useState('components');
  const [history, setHistory] = useState(null);
  const [notes, setNotes] = useState([]);
  const [noteInput, setNoteInput] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [loading, setLoading] = useState(true);
  // Cost simulator
  const [simPercent, setSimPercent] = useState(5);
  const [simQty, setSimQty] = useState(1);
  const [simResult, setSimResult] = useState(null);
  const [simulating, setSimulating] = useState(false);
  // Print
  const [printOpen, setPrintOpen] = useState(false);
  const [sendRdOpen, setSendRdOpen] = useState(false);
  // Inline edit / save & lock
  const [editingLines, setEditingLines] = useState(false);
  const [draftLines, setDraftLines] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [newLine, setNewLine] = useState({ component_sku: '', quantity_per_unit: 1, uom: 'EA', scrap_factor_percent: 0 });
  const [savingDraft, setSavingDraft] = useState(false);
  const [confirmRelease, setConfirmRelease] = useState(false);
  // iter76 — engineering-match release gate / admin override state
  const [gateBlocked, setGateBlocked] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [overriding, setOverriding] = useState(false);
  // Diff
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, h, n, items] = await Promise.all([
        api().get(`/boms/${id}/summary`),
        api().get(`/boms/${id}/completion-history`),
        api().get(`/boms/${id}/notes`),
        api().get('/items'),
      ]);
      setSummary(s.data);
      setHistory(h.data);
      setNotes(n.data || []);
      setAllItems(items.data || []);
    } catch {
      toast.error('Failed to load BOM');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [id]);

  // Build a quick lookup of latest unit cost per sku from summary lines
  const draftTotal = (() => {
    if (!summary) return 0;
    const costMap = {};
    summary.lines.forEach((ln) => { if (ln.latest_unit_cost != null) costMap[ln.component_sku] = ln.latest_unit_cost; });
    let total = 0;
    draftLines.forEach((ln) => {
      const cost = costMap[ln.component_sku];
      if (cost == null) return;
      const eff = (parseFloat(ln.quantity_per_unit) || 0) * (1 + (parseFloat(ln.scrap_factor_percent) || 0) / 100);
      total += cost * eff;
    });
    return Math.round(total * 10000) / 10000;
  })();

  const startEditLines = () => {
    if (!summary) return;
    // Strip enrichments (item_name, low_stock, …) so what we send back matches BOMLine schema
    const seed = summary.lines.map((ln) => ({
      id: ln.id,
      component_sku: ln.component_sku,
      quantity_per_unit: ln.quantity_per_unit,
      uom: ln.uom,
      scrap_factor_percent: ln.scrap_factor_percent || 0,
      substitutes_allowed: ln.substitutes_allowed || false,
    }));
    setDraftLines(seed);
    setEditingLines(true);
  };

  const cancelEditLines = () => {
    setEditingLines(false);
    setDraftLines([]);
    setNewLine({ component_sku: '', quantity_per_unit: 1, uom: 'EA', scrap_factor_percent: 0 });
  };

  const addDraftLine = () => {
    if (!newLine.component_sku) {
      toast.error('Pick a component');
      return;
    }
    if (draftLines.some((l) => l.component_sku === newLine.component_sku)) {
      toast.error('Component already on the BOM');
      return;
    }
    setDraftLines((prev) => [...prev, { ...newLine, quantity_per_unit: parseFloat(newLine.quantity_per_unit) || 1 }]);
    setNewLine({ component_sku: '', quantity_per_unit: 1, uom: 'EA', scrap_factor_percent: 0 });
  };

  const removeDraftLine = (sku) => {
    setDraftLines((prev) => prev.filter((l) => l.component_sku !== sku));
  };

  const updateDraftLineField = (sku, field, value) => {
    setDraftLines((prev) => prev.map((l) => (l.component_sku === sku ? { ...l, [field]: value } : l)));
  };

  const persistDraft = async (alsoRelease = false) => {
    if (!summary) return;
    setSavingDraft(true);
    try {
      // PUT the BOM with normalized lines (NOT status — status is set via /release so the gate is enforced)
      const payload = {
        product_type_id: summary.bom.product_type_id,
        bom_variant_code: summary.bom.bom_variant_code,
        bom_revision: summary.bom.bom_revision,
        status: summary.bom.status,
        effective_date: summary.bom.effective_date,
        notes: summary.bom.notes || '',
        next_revision_date: summary.bom.next_revision_date,
        developed_by: summary.bom.developed_by,
        description: summary.bom.description,
        // iter74 — preserve gate flag so PUT doesn't unset it
        require_matched_file_to_release: !!summary.bom.require_matched_file_to_release,
        // Preserve lineage / archive flags so the server doesn't drop them
        parent_bom_id: summary.bom.parent_bom_id,
        archived: summary.bom.archived,
        archived_at: summary.bom.archived_at,
        archived_by: summary.bom.archived_by,
        // iter78t — per-BOM default label size for the WO completion label
        default_label_size_id: summary.bom.default_label_size_id || null,
        photos: summary.bom.photos || null,
        lines: draftLines.map((l) => ({
          component_sku: l.component_sku,
          quantity_per_unit: parseFloat(l.quantity_per_unit) || 0,
          uom: l.uom || 'EA',
          scrap_factor_percent: parseFloat(l.scrap_factor_percent) || 0,
          substitutes_allowed: !!l.substitutes_allowed,
        })),
      };
      await api().put(`/boms/${id}`, payload);
      if (!alsoRelease) {
        toast.success('Components saved.');
        setEditingLines(false);
        setConfirmRelease(false);
        setDraftLines([]);
        fetchAll();
        return;
      }
      // iter76 — go through gated release endpoint so engineering-match is enforced
      try {
        await api().put(`/boms/${id}/release`);
        toast.success('Saved & released — BOM is now locked.');
        setEditingLines(false);
        setConfirmRelease(false);
        setGateBlocked(false);
        setDraftLines([]);
        fetchAll();
      } catch (relErr) {
        const detail = relErr.response?.data?.detail || 'Release failed';
        if (typeof detail === 'string' && detail.startsWith('Engineering match required')) {
          // Surface the Admin Override UI inside the confirm dialog
          setGateBlocked(true);
          toast.error('Release blocked: engineering match required. Mark a linked file as Matched, or use Admin Override below.');
        } else {
          toast.error(detail);
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingDraft(false);
    }
  };

  // iter76 — Admin Override for engineering-match release gate
  const submitAdminOverride = async () => {
    if (!overrideReason || overrideReason.trim().length < 5) {
      toast.error('Please provide an override reason (5+ characters) — this is audited.');
      return;
    }
    setOverriding(true);
    try {
      await api().put(`/boms/${id}/release?admin_override=true&override_reason=${encodeURIComponent(overrideReason.trim())}`);
      toast.success('Released with Admin override (audited).');
      setEditingLines(false);
      setConfirmRelease(false);
      setGateBlocked(false);
      setOverrideReason('');
      setDraftLines([]);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Override failed');
    } finally {
      setOverriding(false);
    }
  };

  const fetchDiff = async () => {
    if (diff || !summary?.bom?.parent_bom_id) return;
    setDiffLoading(true);
    try {
      const { data } = await api().get(`/boms/${id}/diff`);
      setDiff(data);
    } catch {
      toast.error('Could not load diff');
    } finally {
      setDiffLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const { data } = await api().post(`/boms/${id}/simulate-cost`, {
        percent_change: parseFloat(simPercent) || 0,
        quantity: parseInt(simQty) || 1,
      });
      setSimResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const addNote = async () => {
    if (!noteInput.trim()) return;
    setSavingNote(true);
    try {
      await api().post(`/boms/${id}/notes`, { content: noteInput.trim() });
      setNoteInput('');
      const n = await api().get(`/boms/${id}/notes`);
      setNotes(n.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not add note');
    } finally {
      setSavingNote(false);
    }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api().delete(`/boms/${id}/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not delete note');
    }
  };

  const printTicket = async (unit) => {
    try {
      const { data } = await api().get(`/finished-units/${unit.id}/production-ticket`);
      // iter78u — derive QR from current origin so it scans correctly regardless of env
      const liveQr = typeof window !== 'undefined' && window.location?.origin
        ? `${window.location.origin}/boms/view/${data.bom?.id || ''}`
        : (data.qr_target || data.serial_number || '');
      const qrTarget = liveQr;
      const html = `
        <!doctype html><html><head><title>Production Ticket — ${data.serial_number}</title>
        <style>
          body{font-family: ui-sans-serif, system-ui, sans-serif; margin:0; padding:24px; color:#0f172a;}
          h1{font-size:18pt;margin:0 0 4px;}
          .meta{color:#475569;font-size:10pt;margin-bottom:12px;}
          .grid{display:grid;grid-template-columns:1fr 1fr;gap:8px 16px;font-size:11pt;}
          .row{padding:6px 0;border-bottom:1px dashed #e2e8f0;}
          .label{color:#64748b;text-transform:uppercase;font-size:8pt;letter-spacing:0.05em;}
          .val{font-weight:600;}
          .qr{display:flex;justify-content:center;margin:14px 0;}
          .footer{margin-top:18px;font-size:9pt;color:#94a3b8;}
          .sig{margin-top:32px;display:flex;gap:24px;}
          .sig .box{flex:1;border-top:1px solid #475569;padding-top:4px;font-size:9pt;color:#475569;}
          @media print{body{padding:8mm;}}
        </style></head><body>
          <!--LETTERHEAD-->
          <div class="meta">${data.product_type?.product_type_name || ''} · ${data.bom?.variant_code || ''} rev ${data.bom?.revision || ''}</div>
          <div class="grid">
            <div class="row"><div class="label">Serial Number</div><div class="val mono">${data.serial_number}</div></div>
            <div class="row"><div class="label">Build Date</div><div class="val">${fmtDateTime(data.build_date)}</div></div>
            <div class="row"><div class="label">Built By</div><div class="val">${data.built_by?.name || data.built_by?.id || '—'}</div></div>
            <div class="row"><div class="label">Work Order</div><div class="val mono">${data.work_order?.work_order_number || '—'}</div></div>
            <div class="row"><div class="label">Status</div><div class="val">${data.current_status || '—'}</div></div>
            <div class="row"><div class="label">Location</div><div class="val">${data.current_location || '—'}</div></div>
          </div>
          <div class="qr" id="qr">${renderToStaticMarkup(<QRCodeSVG value={qrTarget} size={140} level="M" includeMargin={false} />)}</div>
          <div class="meta" style="text-align:center">${qrTarget}</div>
          ${data.notes ? `<div class="row"><div class="label">Notes</div><div>${data.notes}</div></div>` : ''}
          <div class="sig">
            <div class="box">QC Sign-off</div>
            <div class="box">Released By</div>
          </div>
        </body></html>`;
      const w = await openLetterheadWindow(html, { title: 'Production Travelling Ticket', features: 'width=800,height=1000' });
      if (!w) { toast.error('Allow pop-ups to print the ticket.'); return; }
      setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 250);
    } catch (e) {
      toast.error('Could not load ticket');
    }
  };

  if (loading) return <div className="flex items-center justify-center h-96"><div className="spinner" /></div>;
  if (!summary) {
    return (
      <div className="text-center py-16 text-slate-500" data-testid="bom-not-found">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
        BOM not found.
        <div className="mt-4">
          <Button variant="outline" onClick={() => navigate('/boms')} className="rounded-sm">Back to BOMs</Button>
        </div>
      </div>
    );
  }

  const bom = summary.bom;
  const totals = summary.totals;
  const lines = summary.lines;
  // iter78u — Always derive QR from current browser origin (not stale backend-stamped FRONTEND_URL)
  const liveBomQrTarget = typeof window !== 'undefined' && window.location?.origin
    ? `${window.location.origin}/boms/view/${bom.id}`
    : (bom.qr_target || `bom:${bom.id}`);
  const printPayload = {
    id: bom.id,
    item_sku: bom.bom_variant_code,
    item_name: `${summary.bom.product_type_name || ''} · rev ${bom.bom_revision}`,
    vendor_sku: bom.developed_by || '',
    barcode_or_qr_value: liveBomQrTarget,
  };

  return (
    <div className="space-y-6 animate-slideIn" data-testid="bom-detail-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate('/boms')} className="rounded-sm" data-testid="bom-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-slate-900 truncate flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-orange-600" />
              <span data-testid="bom-detail-name">{bom.bom_variant_code}</span>
              <span className={`status-badge status-${(bom.status || 'draft').toLowerCase()}`}>{bom.status}</span>
              {bom.rd_rework && <span className="text-[11px] font-medium px-2 py-0.5 rounded bg-fuchsia-100 text-fuchsia-700 border border-fuchsia-200" data-testid="bom-in-rework-badge">In R&D Rework</span>}
            </h1>
            <p className="text-slate-500 text-sm">
              {summary.bom.product_type_name || '—'} · revision <span className="mono">{bom.bom_revision}</span>
              {bom.developed_by && <> · by <span className="text-slate-700">{bom.developed_by}</span></>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {bom.status !== 'Obsolete' && !bom.archived && !bom.rd_rework && ['Admin', 'Engineer'].includes(currentUser?.role) && (
            <Button variant="outline" onClick={() => setSendRdOpen(true)} className="rounded-sm text-fuchsia-700 hover:bg-fuchsia-50" data-testid="bom-send-to-rd-btn">
              <FlaskConical className="w-4 h-4 mr-2" /> Send to R&D Rework
            </Button>
          )}
          {bom.status !== 'Obsolete' && !bom.rd_rework && (
            <Button
              variant="outline"
              onClick={async () => {
                if (!window.confirm(`Create a new revision of ${bom.bom_variant_code} rev ${bom.bom_revision}? The current version will be obsoleted.`)) return;
                try {
                  const { data } = await api().post(`/boms/${id}/revise`, {});
                  toast.success(`Revised → rev ${data.bom_revision}`);
                  navigate(`/boms/view/${data.id}`);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || 'Revise failed');
                }
              }}
              className="rounded-sm text-orange-600 hover:bg-orange-50"
              data-testid="bom-revise-btn"
            >
              <GitBranch className="w-4 h-4 mr-2" /> Revise
            </Button>
          )}
          <Button variant="outline" onClick={() => setPrintOpen(true)} className="rounded-sm" data-testid="bom-print-label-btn">
            <Printer className="w-4 h-4 mr-2" /> Print BOM Label
          </Button>
        </div>
      </div>

      <SendToRDDialog open={sendRdOpen} onClose={() => setSendRdOpen(false)} bom={bom} />
      {bom.rd_rework && (
        <div className="rounded-sm border-2 border-fuchsia-300 bg-fuchsia-50 px-4 py-3 flex items-start gap-3" data-testid="bom-rework-banner">
          <FlaskConical className="w-5 h-5 text-fuchsia-700 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0 text-sm text-fuchsia-900">
            <div className="font-medium">In R&D Rework — still usable in production</div>
            <div className="text-xs text-fuchsia-800 mt-0.5">
              Sent to <b>{bom.rd_rework.project_name}</b> by {bom.rd_rework.started_by_name} on {fmtDate(bom.rd_rework.started_at)}.
              When R&D promotes the draft it becomes rev <span className="mono">{bom.rd_rework.target_revision}</span> and this revision is marked Obsolete.
              Revise is disabled while the rework is in progress.
            </div>
          </div>
          <Button size="sm" variant="outline" className="rounded-sm shrink-0" onClick={() => navigate(`/rd/${bom.rd_rework.project_id}?tab=boms`)} data-testid="bom-rework-open-rd-btn">
            Open in R&D
          </Button>
        </div>
      )}

      {/* iter76 — Prominent banner when this Draft BOM has the engineering-match gate ON */}
      {bom.status === 'Draft' && bom.require_matched_file_to_release && (() => {
        const linkedFiles = summary.bom.linked_files || [];
        const matched = linkedFiles.filter(f => f.matched).length;
        const satisfied = matched > 0;
        return (
          <div
            className={`rounded-sm border-2 px-4 py-3 flex items-start gap-3 ${satisfied ? 'border-emerald-300 bg-emerald-50' : 'border-amber-400 bg-amber-50'}`}
            data-testid="bom-gate-page-banner"
          >
            {satisfied ? <ShieldCheck className="w-5 h-5 text-emerald-700 shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />}
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${satisfied ? 'text-emerald-900' : 'text-amber-900'}`}>
                {satisfied
                  ? `Engineering match satisfied — ${matched} matched file(s). This BOM may be released.`
                  : 'Engineering match required before release'}
              </div>
              <div className={`text-xs ${satisfied ? 'text-emerald-800' : 'text-amber-800'} mt-0.5`}>
                {satisfied
                  ? 'Click "Edit Components" then "Save & Release" to lock the revision.'
                  : <>Mark at least one linked file as <strong>Matched</strong> in the Linked Files tab below{currentUser?.role === 'Admin' ? ', or use the audited Admin Override below.' : ', or ask an Admin to override.'}</>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setActiveTab('linked')}
                className="rounded-sm"
                data-testid="bom-gate-banner-goto-linked"
              >
                <Link2 className="w-4 h-4 mr-1" /> Linked Files
              </Button>
              {!satisfied && currentUser?.role === 'Admin' && (
                <Button
                  size="sm"
                  onClick={() => { setGateBlocked(true); setConfirmRelease(true); }}
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                  data-testid="bom-gate-banner-override-btn"
                >
                  <ShieldCheck className="w-4 h-4 mr-1" /> Admin Override
                </Button>
              )}
            </div>
          </div>
        );
      })()}

      {/* QR + meta + KPIs */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="industrial-card lg:col-span-1">
          <CardContent className="p-5 space-y-4">
            <div className="flex flex-col items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm p-4">
              <QRCodeSVG value={liveBomQrTarget} size={170} level="M" includeMargin={false} data-testid="bom-detail-qr" />
              <div className="text-xs text-slate-500 text-center break-all mono">{liveBomQrTarget}</div>
              <p className="text-xs text-slate-400 text-center">Scan to open this BOM summary.</p>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Created</div>
                <div className="text-slate-800" data-testid="bom-meta-created">{fmtDate(bom.created_at)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Effective</div>
                <div className="text-slate-800">{fmtDate(bom.effective_date)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Calendar className="w-3 h-3" /> Next Revision</div>
                <div className="text-slate-800" data-testid="bom-meta-next-revision">{fmtDate(bom.next_revision_date)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><User className="w-3 h-3" /> Developed By</div>
                <div className="text-slate-800" data-testid="bom-meta-developed-by">{bom.developed_by || '—'}</div>
              </div>
              <div className="col-span-2 md:col-span-1">
                <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><Tag className="w-3 h-3" /> Default Label Size</div>
                <BomLabelSizePicker
                  bom={bom}
                  onSaved={() => fetchAll()}
                />
              </div>
              {bom.description && (
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500">Description</div>
                  <div className="text-slate-800 text-sm">{bom.description}</div>
                </div>
              )}
              {bom.parent_bom_id && (
                <div className="col-span-2">
                  <div className="text-xs uppercase tracking-wider text-slate-500 flex items-center gap-1"><GitBranch className="w-3 h-3" /> Revised From</div>
                  <button
                    type="button"
                    onClick={() => navigate(`/boms/view/${bom.parent_bom_id}`)}
                    className="text-orange-600 hover:underline text-sm mono"
                    data-testid="bom-parent-link"
                  >
                    Open previous revision
                  </button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="industrial-card lg:col-span-2">
          <CardContent className="p-5 space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <StatCard icon={Boxes}      label="Components"     value={totals.lines} />
              <StatCard
                icon={DollarSign}
                label="Unit Cost"
                value={totals.lines > 0 && totals.missing_cost_lines === totals.lines ? '—' : `$${totals.total_unit_cost}`}
                subtle={totals.missing_cost_lines > 0 ? `${totals.missing_cost_lines} missing cost` : 'all priced'}
                accent="text-orange-600"
              />
              <StatCard icon={Hash}       label="Work Orders"     value={totals.work_orders} subtle={`${totals.in_progress_orders} in progress`} />
              <StatCard icon={CheckCircle2} label="Finished Units" value={totals.finished_units} subtle={`${totals.completed_quantity}/${totals.target_quantity} target`} accent="text-emerald-700" />
            </div>
            {totals.low_stock_components > 0 && (
              <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded-sm p-2 flex items-center gap-2" data-testid="bom-low-stock-warning">
                <AlertTriangle className="w-4 h-4" />
                {totals.low_stock_components} component{totals.low_stock_components === 1 ? '' : 's'} below reorder point
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <RolledCostCard bomId={bom.id} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="components" data-testid="bom-tab-components">Components</TabsTrigger>
          <TabsTrigger value="completion" data-testid="bom-tab-completion">Completion History</TabsTrigger>
          <TabsTrigger value="simulator" data-testid="bom-tab-simulator">What-If Cost</TabsTrigger>
          <TabsTrigger value="notes" data-testid="bom-tab-notes">Notes</TabsTrigger>
          <TabsTrigger value="linked" data-testid="bom-tab-linked">Linked Files</TabsTrigger>
          <TabsTrigger value="lineage" data-testid="bom-tab-lineage">Lineage</TabsTrigger>
          {summary.bom.parent_bom_id && (
            <TabsTrigger value="diff" data-testid="bom-tab-diff" onClick={fetchDiff}>Diff Previous</TabsTrigger>
          )}
        </TabsList>

        {/* Components / total cost */}
        <TabsContent value="components" className="mt-4">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <Layers className="w-4 h-4" /> Components & Total BOM Price
              </h2>
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500 mono" data-testid="bom-components-total">
                  Total / unit: <span className="font-medium text-slate-800">${editingLines ? draftTotal : totals.total_unit_cost}</span>
                </span>
                {bom.status === 'Draft' && !editingLines && (
                  <Button
                    onClick={startEditLines}
                    size="sm"
                    variant="outline"
                    className="rounded-sm"
                    data-testid="bom-edit-components-btn"
                  >
                    Edit Components
                  </Button>
                )}
                {editingLines && (
                  <div className="flex items-center gap-2">
                    <Button onClick={cancelEditLines} variant="ghost" size="sm" className="rounded-sm" data-testid="bom-cancel-edit-btn">
                      Cancel
                    </Button>
                    <Button
                      onClick={() => persistDraft(false)}
                      size="sm"
                      variant="outline"
                      disabled={savingDraft}
                      className="rounded-sm"
                      data-testid="bom-save-draft-btn"
                    >
                      Save Draft
                    </Button>
                    <Button
                      onClick={() => setConfirmRelease(true)}
                      size="sm"
                      disabled={savingDraft || draftLines.length === 0}
                      className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
                      data-testid="bom-save-release-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Save & Release
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {editingLines ? (
                <div className="p-4 space-y-4" data-testid="bom-edit-mode">
                  <div className="border border-slate-200 rounded-sm p-3 bg-slate-50 space-y-2">
                    <div className="text-xs uppercase tracking-wider text-slate-500">Add Component</div>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end">
                      <div className="md:col-span-5">
                        <select
                          value={newLine.component_sku}
                          onChange={(e) => setNewLine({ ...newLine, component_sku: e.target.value, uom: (allItems.find(i => i.item_sku === e.target.value) || {}).base_uom || 'EA' })}
                          className="w-full rounded-sm border border-slate-300 px-2 py-1 text-sm bg-white"
                          data-testid="bom-add-component-select"
                        >
                          <option value="">Pick item…</option>
                          {allItems
                            .filter((it) => !draftLines.some(l => l.component_sku === it.item_sku))
                            .map((it) => (
                              <option key={it.id} value={it.item_sku}>{it.item_sku} — {it.item_name}</option>
                            ))}
                        </select>
                      </div>
                      <div className="md:col-span-2">
                        <Input
                          type="number"
                          step="0.001"
                          value={newLine.quantity_per_unit}
                          onChange={(e) => setNewLine({ ...newLine, quantity_per_unit: e.target.value })}
                          className="rounded-sm mono"
                          placeholder="Qty"
                          data-testid="bom-add-component-qty"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Input
                          value={newLine.uom}
                          onChange={(e) => setNewLine({ ...newLine, uom: e.target.value })}
                          className="rounded-sm mono"
                          placeholder="UOM"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Input
                          type="number"
                          value={newLine.scrap_factor_percent}
                          onChange={(e) => setNewLine({ ...newLine, scrap_factor_percent: e.target.value })}
                          className="rounded-sm mono"
                          placeholder="Scrap %"
                        />
                      </div>
                      <div className="md:col-span-1">
                        <Button onClick={addDraftLine} className="w-full rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="bom-add-component-btn">
                          Add
                        </Button>
                      </div>
                    </div>
                  </div>
                  <Table className="data-table">
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead className="text-right">Qty / unit</TableHead>
                        <TableHead className="text-right">Scrap %</TableHead>
                        <TableHead className="text-right">Effective × $unit</TableHead>
                        <TableHead className="text-right">Line $</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {draftLines.length === 0 && (
                        <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">No components yet — add at least one above.</TableCell></TableRow>
                      )}
                      {draftLines.map((ln) => {
                        const item = allItems.find((i) => i.item_sku === ln.component_sku);
                        const summaryLine = summary.lines.find((l) => l.component_sku === ln.component_sku);
                        const cost = summaryLine?.latest_unit_cost;
                        const eff = (parseFloat(ln.quantity_per_unit) || 0) * (1 + (parseFloat(ln.scrap_factor_percent) || 0) / 100);
                        const lineTotal = cost != null ? Math.round(cost * eff * 100) / 100 : null;
                        return (
                          <TableRow key={ln.component_sku} data-testid={`bom-draft-line-${ln.component_sku}`}>
                            <TableCell className="mono">{ln.component_sku}</TableCell>
                            <TableCell>{item?.item_name || '—'}</TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                step="0.001"
                                value={ln.quantity_per_unit}
                                onChange={(e) => updateDraftLineField(ln.component_sku, 'quantity_per_unit', e.target.value)}
                                className="w-24 ml-auto rounded-sm mono text-right"
                              />
                            </TableCell>
                            <TableCell className="text-right">
                              <Input
                                type="number"
                                value={ln.scrap_factor_percent}
                                onChange={(e) => updateDraftLineField(ln.component_sku, 'scrap_factor_percent', e.target.value)}
                                className="w-20 ml-auto rounded-sm mono text-right"
                              />
                            </TableCell>
                            <TableCell className="text-right mono text-slate-500 text-xs">
                              {eff.toFixed(3)} × {cost != null ? `$${cost}` : '—'}
                            </TableCell>
                            <TableCell className="text-right mono font-medium">{lineTotal != null ? `$${lineTotal}` : <span className="text-slate-300">—</span>}</TableCell>
                            <TableCell className="text-right">
                              <Button variant="ghost" size="sm" onClick={() => removeDraftLine(ln.component_sku)} className="text-red-600">
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  <div className="text-right text-sm">
                    <span className="text-slate-500">Live total / unit:</span>{' '}
                    <span className="text-lg font-bold text-slate-900 mono" data-testid="bom-edit-live-total">${draftTotal}</span>
                  </div>
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>SKU</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Scrap %</TableHead>
                      <TableHead className="text-right">Effective Qty</TableHead>
                      <TableHead className="text-right">Unit $</TableHead>
                      <TableHead className="text-right">Line $</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((ln) => (
                      <TableRow key={ln.id} data-testid={`bom-line-row-${ln.component_sku}`}>
                        <TableCell className="mono">
                          <button
                            onClick={() => ln.item_id && navigate(`/items/view/${ln.item_id}`)}
                            className="text-slate-800 hover:text-orange-600 hover:underline"
                            disabled={!ln.item_id}
                          >
                            {ln.component_sku}
                          </button>
                          <div><AssemblyLineBadge asm={ln.assembly} /></div>
                        </TableCell>
                        <TableCell>{ln.item_name || '—'}</TableCell>
                        <TableCell className="text-slate-500 text-sm">{ln.item_category || '—'}</TableCell>
                        <TableCell className="text-right mono">{ln.quantity_per_unit} {ln.uom}</TableCell>
                        <TableCell className="text-right mono">{ln.scrap_factor_percent || 0}%</TableCell>
                        <TableCell className="text-right mono">{ln.effective_qty}</TableCell>
                        <TableCell className="text-right mono">{ln.latest_unit_cost != null ? `$${ln.latest_unit_cost}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell className="text-right mono font-medium">{ln.line_total != null ? `$${ln.line_total}` : <span className="text-slate-300">—</span>}</TableCell>
                        <TableCell>
                          {ln.low_stock ? (
                            <span className="text-red-600 text-xs flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> low</span>
                          ) : (
                            <span className="text-slate-400 text-xs">ok</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Completion history */}
        <TabsContent value="completion" className="mt-4">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <History className="w-4 h-4" /> Completion History
              </h2>
              {history && (
                <span className="text-xs text-slate-500 mono" data-testid="bom-completion-totals">
                  {history.totals.completed} unit(s) · {history.totals.in_stock} in stock · {history.totals.shipped} shipped · {history.totals.scrapped} scrapped
                </span>
              )}
            </CardHeader>
            <CardContent className="p-0">
              {!history || history.units.length === 0 ? (
                <div className="p-8 text-center text-sm text-slate-500" data-testid="bom-completion-empty">
                  No finished units recorded under this BOM yet.
                </div>
              ) : (
                <Table className="data-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Serial</TableHead>
                      <TableHead>Build Date</TableHead>
                      <TableHead>Built By</TableHead>
                      <TableHead>Work Order</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {history.units.map((u) => (
                      <TableRow key={u.id} data-testid={`bom-completion-row-${u.id}`}>
                        <TableCell className="mono">
                          <button onClick={() => navigate(`/units/${u.id}/genealogy`)} className="text-slate-800 hover:text-orange-600 hover:underline">
                            {u.serial_number}
                          </button>
                        </TableCell>
                        <TableCell className="text-sm text-slate-600">{fmtDateTime(u.build_date)}</TableCell>
                        <TableCell className="text-sm text-slate-700" data-testid={`bom-completion-user-${u.id}`}>{u.built_by_name || u.built_by_user || '—'}</TableCell>
                        <TableCell className="text-sm mono text-slate-500">{u.work_order_number || '—'}</TableCell>
                        <TableCell>
                          <span className={`status-badge status-${(u.current_status || '').toLowerCase()}`}>{u.current_status}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => printTicket(u)}
                            className="rounded-sm"
                            data-testid={`bom-print-ticket-${u.id}`}
                            title="Print travelling ticket"
                          >
                            <QrCode className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cost simulator */}
        <TabsContent value="simulator" className="mt-4">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <DollarSign className="w-4 h-4" /> What-If Cost Simulator
              </h2>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Cost Δ %</label>
                  <Input
                    type="number"
                    value={simPercent}
                    onChange={(e) => setSimPercent(e.target.value)}
                    className="rounded-sm mono"
                    data-testid="bom-sim-percent-input"
                  />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-slate-500 block mb-1">Quantity</label>
                  <Input
                    type="number"
                    value={simQty}
                    onChange={(e) => setSimQty(e.target.value)}
                    className="rounded-sm mono"
                    data-testid="bom-sim-quantity-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={runSimulation} disabled={simulating} className="bg-orange-600 hover:bg-orange-700 rounded-sm w-full" data-testid="bom-sim-run-btn">
                    {simulating ? 'Simulating…' : 'Run Simulation'}
                  </Button>
                </div>
              </div>
              {simResult && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2" data-testid="bom-sim-result">
                  <StatCard icon={DollarSign} label="Current / unit" value={`$${simResult.current_unit_cost}`} />
                  <StatCard icon={DollarSign} label="Simulated / unit" value={`$${simResult.simulated_unit_cost}`}
                            subtle={`${simResult.delta_per_unit >= 0 ? '+' : ''}${simResult.delta_per_unit}`}
                            accent={simResult.delta_per_unit > 0 ? 'text-red-600' : 'text-emerald-700'} />
                  <StatCard icon={DollarSign} label={`Extended × ${simResult.quantity}`} value={`$${simResult.simulated_extended_cost}`}
                            subtle={`Δ ${simResult.delta_extended_cost >= 0 ? '+' : ''}$${simResult.delta_extended_cost}`}
                            accent={simResult.delta_extended_cost > 0 ? 'text-red-600' : 'text-emerald-700'} />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notes */}
        <TabsContent value="notes" className="mt-4">
          <Card className="industrial-card">
            <CardHeader className="border-b border-slate-100 pb-3">
              <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                <FileText className="w-4 h-4" /> Notes ({notes.length})
              </h2>
            </CardHeader>
            <CardContent className="p-5 space-y-4">
              <div className="space-y-2">
                <Textarea
                  value={noteInput}
                  onChange={(e) => setNoteInput(e.target.value)}
                  placeholder="Add a note about this BOM…"
                  className="rounded-sm"
                  data-testid="bom-note-input"
                />
                <div className="flex justify-end">
                  <Button onClick={addNote} disabled={savingNote || !noteInput.trim()} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="bom-add-note-btn">
                    <Send className="w-4 h-4 mr-1" /> {savingNote ? 'Saving…' : 'Add note'}
                  </Button>
                </div>
              </div>
              {notes.length === 0 ? (
                <div className="text-center py-6 text-sm text-slate-400" data-testid="bom-notes-empty">No notes yet.</div>
              ) : (
                <ul className="space-y-2" data-testid="bom-notes-list">
                  {notes.map((n) => (
                    <li key={n.id} className="border border-slate-200 rounded-sm p-3 text-sm" data-testid={`bom-note-${n.id}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs text-slate-500">
                            <span className="font-medium text-slate-700">{n.author_name}</span>
                            <span className="ml-2">{fmtDateTime(n.created_at)}</span>
                          </div>
                          <div className="mt-1 text-slate-800 whitespace-pre-wrap">{n.content}</div>
                        </div>
                        {(currentUser?.role === 'Admin' || currentUser?.id === n.author_id) && (
                          <Button variant="ghost" size="sm" onClick={() => deleteNote(n.id)} className="text-red-600 rounded-sm" data-testid={`bom-delete-note-${n.id}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        {/* iter73 — Linked Production Files */}
        <TabsContent value="linked" className="mt-4">
          <BomLinkedFilesPanel
            api={api}
            bomId={summary.bom.id}
            files={summary.bom.linked_files || []}
            onChanged={() => fetchAll()}
            canEdit={currentUser?.role === 'Admin' || currentUser?.role === 'ProductionSupervisor' || currentUser?.role === 'Production Supervisor' || currentUser?.role === 'Engineer'}
            requireMatchedToRelease={!!summary.bom.require_matched_file_to_release}
          />
        </TabsContent>
        <TabsContent value="lineage" className="mt-4">
          <Card className="industrial-card">
            <CardContent className="p-5">
              {activeTab === 'lineage' && <BomLineageTimeline bomId={bom.id} />}
            </CardContent>
          </Card>
        </TabsContent>
        {summary.bom.parent_bom_id && (
          <TabsContent value="diff" className="mt-4">
            <Card className="industrial-card">
              <CardHeader className="border-b border-slate-100 pb-3 flex-row items-center justify-between flex">
                <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                  <GitBranch className="w-4 h-4" /> Diff vs Previous Revision
                </h2>
                {diff?.parent_summary && (
                  <span className="text-xs text-slate-500 mono" data-testid="bom-diff-summary">
                    {diff.parent_summary.bom_variant_code} rev {diff.parent_summary.bom_revision} → rev {diff.child_summary.bom_revision}
                  </span>
                )}
              </CardHeader>
              <CardContent className="p-5 space-y-4">
                {diffLoading && <div className="text-center text-sm text-slate-400 py-6">Computing diff…</div>}
                {!diffLoading && !diff && (
                  <div className="text-center text-sm text-slate-400 py-6">Loading…</div>
                )}
                {!diffLoading && diff && (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <div className="rounded-sm border border-emerald-200 bg-emerald-50 p-3" data-testid="bom-diff-added">
                        <div className="text-xs uppercase tracking-wider text-emerald-700">Added</div>
                        <div className="text-2xl font-bold text-emerald-700 mono">{diff.totals.added}</div>
                      </div>
                      <div className="rounded-sm border border-red-200 bg-red-50 p-3" data-testid="bom-diff-removed">
                        <div className="text-xs uppercase tracking-wider text-red-700">Removed</div>
                        <div className="text-2xl font-bold text-red-700 mono">{diff.totals.removed}</div>
                      </div>
                      <div className="rounded-sm border border-amber-200 bg-amber-50 p-3" data-testid="bom-diff-changed">
                        <div className="text-xs uppercase tracking-wider text-amber-700">Changed</div>
                        <div className="text-2xl font-bold text-amber-700 mono">{diff.totals.changed}</div>
                      </div>
                      <div className="rounded-sm border border-slate-200 p-3" data-testid="bom-diff-cost-delta">
                        <div className="text-xs uppercase tracking-wider text-slate-500">Cost Δ / unit</div>
                        <div className={`text-2xl font-bold mono ${diff.totals.delta_unit_cost > 0 ? 'text-red-600' : diff.totals.delta_unit_cost < 0 ? 'text-emerald-700' : 'text-slate-900'}`}>
                          {diff.totals.delta_unit_cost > 0 ? '+' : ''}${diff.totals.delta_unit_cost}
                        </div>
                      </div>
                    </div>

                    {[
                      { key: 'added', label: 'Added', tone: 'emerald', rows: diff.added },
                      { key: 'removed', label: 'Removed', tone: 'red', rows: diff.removed },
                      { key: 'changed', label: 'Changed', tone: 'amber', rows: diff.changed },
                    ].map((sec) => (
                      <div key={sec.key}>
                        <div className={`text-xs uppercase tracking-wider mb-1 text-${sec.tone}-700`}>{sec.label}</div>
                        {sec.rows.length === 0 ? (
                          <div className="text-xs text-slate-400 py-2">None</div>
                        ) : (
                          <ul className="text-sm divide-y divide-slate-100 border border-slate-200 rounded-sm">
                            {sec.rows.map((r, i) => (
                              <li key={`${sec.key}-${r.component_sku}-${i}`} className="px-3 py-2 flex items-start justify-between gap-3" data-testid={`bom-diff-row-${sec.key}-${r.component_sku}`}>
                                <div className="min-w-0">
                                  <div className="mono font-medium">{r.component_sku}</div>
                                  <div className="text-xs text-slate-500">{r.item_name || '—'}</div>
                                  {sec.key === 'changed' && (
                                    <div className="text-xs text-slate-500 mt-1">
                                      {r.changed_fields.map((f) => (
                                        <span key={f} className="mr-2">
                                          <span className="text-slate-400">{f}:</span>{' '}
                                          {String(r.parent?.[f] ?? '—')} → <span className="text-slate-800">{String(r.child?.[f] ?? '—')}</span>
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="text-right text-xs mono shrink-0">
                                  {sec.key === 'added' && r.child_line_total != null && <span className="text-emerald-700">+${r.child_line_total}</span>}
                                  {sec.key === 'removed' && r.parent_line_total != null && <span className="text-red-700">-${r.parent_line_total}</span>}
                                  {sec.key === 'changed' && (r.parent_line_total != null && r.child_line_total != null) && (
                                    <span className={r.child_line_total > r.parent_line_total ? 'text-red-600' : 'text-emerald-700'}>
                                      ${r.parent_line_total} → ${r.child_line_total}
                                    </span>
                                  )}
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>
      <Dialog open={confirmRelease} onOpenChange={(o) => { setConfirmRelease(o); if (!o) { setGateBlocked(false); setOverrideReason(''); } }}>
        <DialogContent className="sm:max-w-md" data-testid="bom-release-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" /> Save & Release Revision
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-slate-700">
            <p>
              Releasing this revision will <span className="font-medium">lock</span> the BOM:
              once a work order references it, components and quantities can no longer be modified
              (only metadata fields like notes and next revision date stay editable).
            </p>
            <div className="rounded-sm border border-slate-200 bg-slate-50 p-3 text-xs">
              <div className="flex justify-between"><span>Components:</span><span className="mono">{draftLines.length}</span></div>
              <div className="flex justify-between"><span>Total / unit:</span><span className="mono font-medium">${draftTotal}</span></div>
            </div>

            {/* iter76 — Admin Override panel surfaces only when gate blocked the release */}
            {gateBlocked && (
              <div className="rounded-sm border-2 border-amber-400 bg-amber-50 p-3 space-y-2" data-testid="bom-admin-override-panel">
                <div className="flex items-center gap-2 text-amber-800 font-medium text-sm">
                  <AlertTriangle className="w-4 h-4" />
                  Engineering Match Required
                </div>
                <p className="text-xs text-amber-900">
                  This BOM requires at least one linked file marked <strong>Matched</strong> before it can be released.
                  Ask an engineer to confirm a CAD/schematic/weld drawing in the <strong>Linked Files</strong> tab,
                  {currentUser?.role === 'Admin' ? ' or use the Admin Override below (audited).' : ' or ask an Admin to override.'}
                </p>
                {currentUser?.role === 'Admin' && (
                  <>
                    <div className="space-y-1">
                      <label className="text-[11px] uppercase tracking-wider text-amber-900 font-medium">Override Reason (required, audited)</label>
                      <Textarea
                        rows={2}
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        placeholder="e.g., Customer urgency — drawings will follow within 24h"
                        className="rounded-sm text-xs border-amber-300 focus-visible:ring-amber-400"
                        data-testid="bom-admin-override-reason-input"
                      />
                    </div>
                    <Button
                      onClick={submitAdminOverride}
                      disabled={overriding}
                      className="w-full bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                      data-testid="bom-admin-override-btn"
                    >
                      <ShieldCheck className="w-4 h-4 mr-1" /> {overriding ? 'Overriding…' : 'Release with Admin Override'}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => { setConfirmRelease(false); setGateBlocked(false); setOverrideReason(''); }} className="rounded-sm">Cancel</Button>
            <Button
              onClick={() => persistDraft(true)}
              disabled={savingDraft || gateBlocked}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-sm"
              data-testid="bom-release-confirm-btn"
            >
              <CheckCircle2 className="w-4 h-4 mr-1" /> {savingDraft ? 'Saving…' : 'Save & Release'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <PrintLabelDialog
        open={printOpen}
        onOpenChange={setPrintOpen}
        item={printPayload}
        labelType="BOM"
      />
    </div>
  );
};

export default BomDetailPage;
